/** Managing share links.
 *
 * Revoked and expired links stay on the list. They are the record of what was shared with
 * whom, and hiding them would leave nobody able to answer that question later.
 */

import { useState } from 'react'
import { Check, Copy, Link2 } from 'lucide-react'

import {
  Button,
  EmptyState,
  ErrorState,
  Field,
  Loading,
  Pill,
  SectionHeader,
  inputClass,
} from '@/components/ui'
import { useProjects } from '@/features/projects/useProjects'
import {
  shareUrl,
  useCreateShare,
  useRevokeShare,
  useShares,
  type ShareLink,
} from '@/features/shares/useShares'

function CopyButton({ token }: { token: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <Button
      onClick={() => {
        void navigator.clipboard.writeText(shareUrl(token)).then(() => {
          setCopied(true)
          window.setTimeout(() => setCopied(false), 1500)
        })
      }}
    >
      {copied ? <Check size={14} aria-hidden /> : <Copy size={14} aria-hidden />}
      {copied ? 'Copied' : 'Copy link'}
    </Button>
  )
}

function CreateForm() {
  const create = useCreateShare()
  const { data: projects } = useProjects({})
  const [kind, setKind] = useState<'project' | 'status'>('project')
  const [projectId, setProjectId] = useState('')
  const [label, setLabel] = useState('')
  const [ttl, setTtl] = useState(90)

  const list = projects?.projects ?? []
  const disabled = create.isPending || (kind === 'project' && !projectId)

  return (
    <div className="card p-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="What to share">
          <select
            className={inputClass}
            value={kind}
            onChange={(event) => setKind(event.target.value as 'project' | 'status')}
          >
            <option value="project">One project</option>
            <option value="status">Portfolio status</option>
          </select>
        </Field>
        {kind === 'project' ? (
          <Field label="Project">
            <select
              className={inputClass}
              value={projectId}
              onChange={(event) => setProjectId(event.target.value)}
            >
              <option value="">Choose…</option>
              {list.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </Field>
        ) : (
          <Field label="Included" hint="Health, progress and target dates. No notes or history.">
            <p className="py-1.5 text-sm text-fg-muted">Every dated, non-cancelled project</p>
          </Field>
        )}
        <Field label="Who it is for" hint="Shown on this list, not on the shared page.">
          <input
            className={inputClass}
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            placeholder="Finance, August review"
          />
        </Field>
        <Field label="Expires after" hint="A link that never expires is one nobody remembers.">
          <select
            className={inputClass}
            value={ttl}
            onChange={(event) => setTtl(Number(event.target.value))}
          >
            {[7, 30, 90, 180, 365].map((days) => (
              <option key={days} value={days}>
                {days} days
              </option>
            ))}
          </select>
        </Field>
      </div>
      <div className="mt-3 flex items-center gap-3">
        <Button
          variant="primary"
          disabled={disabled}
          onClick={() =>
            create.mutate(
              {
                kind,
                project_id: kind === 'project' ? projectId : null,
                label,
                ttl_days: ttl,
              },
              {
                onSuccess: () => {
                  setLabel('')
                  setProjectId('')
                },
              },
            )
          }
        >
          <Link2 size={14} aria-hidden />
          {create.isPending ? 'Creating…' : 'Create link'}
        </Button>
        {create.isError ? <ErrorState error={create.error} /> : null}
      </div>
    </div>
  )
}

function Row({ link }: { link: ShareLink }) {
  const revoke = useRevokeShare()
  const [confirming, setConfirming] = useState(false)
  const [reason, setReason] = useState('')

  return (
    <li className="px-4 py-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium text-fg">
            {link.label || (link.kind === 'status' ? 'Portfolio status' : 'Project share')}
          </div>
          <div className="text-xs text-fg-muted">
            {link.createdBy} · {new Date(link.createdDate).toLocaleDateString()} ·{' '}
            {link.viewCount} {link.viewCount === 1 ? 'view' : 'views'}
            {link.lastViewedAt
              ? `, last ${new Date(link.lastViewedAt).toLocaleDateString()}`
              : ', never opened'}
          </div>
        </div>
        {link.isActive ? (
          <Pill tone="green">Active</Pill>
        ) : link.revokedAt ? (
          <Pill tone="neutral">Revoked</Pill>
        ) : (
          <Pill tone="amber">Expired</Pill>
        )}
        {link.isActive ? (
          <>
            <CopyButton token={link.token} />
            <Button variant="destructive" onClick={() => setConfirming((value) => !value)}>
              Revoke
            </Button>
          </>
        ) : null}
      </div>

      {confirming ? (
        <div className="mt-3 flex flex-wrap items-end gap-2 border-t border-border pt-3">
          <div className="min-w-[240px] flex-1">
            <Field
              label="Why"
              hint="Revoking is immediate and permanent. Anyone holding the link loses access."
            >
              <input
                className={inputClass}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="Review finished"
              />
            </Field>
          </div>
          <Button
            variant="destructive"
            disabled={!reason.trim() || revoke.isPending}
            onClick={() => revoke.mutate({ id: link.id, reason: reason.trim() })}
          >
            {revoke.isPending ? 'Revoking…' : 'Revoke for good'}
          </Button>
          <Button onClick={() => setConfirming(false)}>Cancel</Button>
        </div>
      ) : null}
    </li>
  )
}

export function SharesPage() {
  const { data, isPending, error } = useShares()

  if (isPending) return <Loading label="Loading share links" />
  if (error) return <ErrorState error={error} />

  const links = data ?? []
  const active = links.filter((link) => link.isActive)

  return (
    <div className="mx-auto max-w-[900px] pt-2">
      <header className="mb-6">
        <h1 className="font-heading text-3xl text-fg">Share links</h1>
        <p className="mt-1 text-sm text-fg-muted">
          Read-only pages for people who do not sign in. Each link carries health, dates and
          progress — never notes, history, or who changed what.
        </p>
      </header>

      <CreateForm />

      <div className="mt-8">
        <SectionHeader>
          {active.length} active of {links.length}
        </SectionHeader>
      </div>

      {links.length === 0 ? (
        <EmptyState title="No links yet">
          Create one above and send the URL to whoever needs it.
        </EmptyState>
      ) : (
        <ul className="card divide-y divide-border">
          {links.map((link) => (
            <Row key={link.id} link={link} />
          ))}
        </ul>
      )}
    </div>
  )
}
